import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import { getList, create } from "../../dataProvider/API";
import {
  GET_GOVERNING_BODIES_ADMIN_ALL,
  GET_GOVERNING_BODIES_ADMIN_SEARCH,
  SAVE_GOVERNING_BODY_ADMIN,
  GET_DD_LISTS,
  GET_POINT_SCHEMAS,
  GET_RULE_GROUPS,
  SEARCH_GOVERNING_BODY_TEAMS,
  LIST_TEAMS,
} from "../../constants/endpoints";
import { ALERT_TEXT } from "../../constants/titles";
import { normalizeGoverningBodiesPayload } from "../../utils/governingBodyApi";

const initialState = {
  loading: false,
  fetchDataGoverningBodies: null,
  pointSchemas: [],
  ruleGroups: [],
  ddLists: [],
  governingBodyTeams: [],
  error: null,
};

const toArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  return (
    payload.dtos ||
    payload.data ||
    payload.items ||
    payload.content ||
    payload.pointSchemas ||
    payload.ruleGroups ||
    payload.teamsDto ||
    payload.governingBodiesDto ||
    []
  );
};

export const getGoverningBodiesAdminList = createAsyncThunk(
  "governingBodies/getGoverningBodiesAdminList",
  async ({ filters, pagination }, { rejectWithValue }) => {
    try {
      const searchKey = String(filters?.query || "").trim();
      const listParams = {
        page: pagination?.page,
        limit: pagination?.limit,
        sort: pagination?.sort,
        isAscending: pagination?.isAscending,
        status: filters?.status,
        teamType: filters?.teamType,
        locale: filters?.locale,
      };
      const endpoint =
        searchKey.length > 0
          ? GET_GOVERNING_BODIES_ADMIN_SEARCH({ key: searchKey, ...listParams })
          : GET_GOVERNING_BODIES_ADMIN_ALL(listParams);
      const payload = await getList(endpoint);
      const normalized = normalizeGoverningBodiesPayload(payload, filters?.locale);

      return {
        payload,
        ...normalized,
      };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Failed to load governing bodies list",
      );
    }
  },
);

export const saveGoverningBodyAdmin = createAsyncThunk(
  "governingBodies/saveGoverningBodyAdmin",
  async ({ data }, { rejectWithValue }) => {
    try {
      const payload = await create(SAVE_GOVERNING_BODY_ADMIN, data);
      return payload;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to save governing body");
    }
  },
);

export const softDeleteGoverningBodyAdmin = createAsyncThunk(
  "governingBodies/softDeleteGoverningBodyAdmin",
  async ({ data }, { rejectWithValue }) => {
    try {
      const payload = await create(SAVE_GOVERNING_BODY_ADMIN, {
        ...data,
        status: "DEPRECATED",
      });
      return payload;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to delete governing body");
    }
  },
);

export const getPointSchemas = createAsyncThunk(
  "governingBodies/getPointSchemas",
  async (_, { rejectWithValue }) => {
    try {
      const payload = await getList(GET_POINT_SCHEMAS);
      return toArray(payload);
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load point schemas");
    }
  },
);

export const getDdLists = createAsyncThunk(
  "governingBodies/getDdLists",
  async (_, { rejectWithValue }) => {
    try {
      const payload = await getList(GET_DD_LISTS);
      return toArray(payload);
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load DD lists");
    }
  },
);

export const getRuleGroups = createAsyncThunk(
  "governingBodies/getRuleGroups",
  async (_, { rejectWithValue }) => {
    try {
      const payload = await getList(GET_RULE_GROUPS);
      return toArray(payload);
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load rule groups");
    }
  },
);

export const searchGoverningBodyTeams = createAsyncThunk(
  "governingBodies/searchGoverningBodyTeams",
  async ({ query, page = 0, limit = 25 }, { rejectWithValue }) => {
    try {
      const params = { page, limit, sort: "ID", isAscending: true };
      // A blank search key only matches names containing a space, so list instead.
      const trimmed = String(query || "").trim();
      const endpoint = trimmed
        ? SEARCH_GOVERNING_BODY_TEAMS({ ...params, query: trimmed })
        : LIST_TEAMS({ ...params, teamType: "G" });
      const payload = await getList(endpoint);
      return toArray(payload);
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load governing body teams");
    }
  },
);

export const searchParentGoverningBodies = createAsyncThunk(
  "governingBodies/searchParentGoverningBodies",
  async ({ query, page = 0, limit = 100, locale = "en-US" }, { rejectWithValue }) => {
    try {
      const searchKey = String(query || "").trim();
      const endpoint =
        searchKey.length > 0
          ? GET_GOVERNING_BODIES_ADMIN_SEARCH({
              key: searchKey,
              page,
              limit,
              sort: "Name",
              isAscending: true,
            })
          : GET_GOVERNING_BODIES_ADMIN_ALL({
              page,
              limit,
              sort: "Name",
              isAscending: true,
            });
      const payload = await getList(endpoint);
      const normalized = normalizeGoverningBodiesPayload(payload, locale);
      return normalized.rows;
    } catch (error) {
      return rejectWithValue(
        error?.message || "Failed to load parent governing bodies",
      );
    }
  },
);

const governingBodiesSlice = createSlice({
  name: "governingBodies",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getGoverningBodiesAdminList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getGoverningBodiesAdminList.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.fetchDataGoverningBodies = action.payload;
      })
      .addCase(getGoverningBodiesAdminList.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to load governing bodies list";
        toast.error(ALERT_TEXT.BAD_REQUEST);
      })

      .addCase(saveGoverningBodyAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveGoverningBodyAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const isEdit = Boolean(action.meta?.arg?.data?.id);
        toast.success(
          isEdit
            ? ALERT_TEXT.SUCCESS_UPDATE_GOVERNING_BODY
            : ALERT_TEXT.SUCCESS_CREATE_GOVERNING_BODY,
        );
      })
      .addCase(saveGoverningBodyAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || "Failed to save";
        toast.error(ALERT_TEXT.BAD_REQUEST);
      })

      .addCase(softDeleteGoverningBodyAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(softDeleteGoverningBodyAdmin.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        toast.success(ALERT_TEXT.SUCCESS_DELETE_GOVERNING_BODY);
      })
      .addCase(softDeleteGoverningBodyAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || action.error?.message || "Failed to delete";
        toast.error(ALERT_TEXT.BAD_REQUEST);
      })

      .addCase(getPointSchemas.fulfilled, (state, action) => {
        state.pointSchemas = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(getPointSchemas.rejected, (state, action) => {
        state.error =
          action.payload || action.error?.message || "Failed to load point schemas";
        toast.error(ALERT_TEXT.BAD_REQUEST);
      })

      .addCase(getRuleGroups.fulfilled, (state, action) => {
        state.ruleGroups = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(getRuleGroups.rejected, (state, action) => {
        state.error =
          action.payload || action.error?.message || "Failed to load rule groups";
        toast.error(ALERT_TEXT.BAD_REQUEST);
      })

      .addCase(getDdLists.fulfilled, (state, action) => {
        state.ddLists = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(getDdLists.rejected, (state, action) => {
        state.error =
          action.payload || action.error?.message || "Failed to load DD lists";
        toast.error(ALERT_TEXT.BAD_REQUEST);
      })

      .addCase(searchGoverningBodyTeams.fulfilled, (state, action) => {
        state.governingBodyTeams = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(searchGoverningBodyTeams.rejected, (state, action) => {
        state.error =
          action.payload || action.error?.message || "Failed to load teams";
        toast.error(ALERT_TEXT.BAD_REQUEST);
      });
  },
});

export default governingBodiesSlice.reducer;
